import * as React from "react";
import {
  FormatterColorMode,
  MFBlockMeasureItem,
  MFBlocksMeasureItem,
  MFMeasure,
  MFSectionMeasureItem,
  MFValue,
  TAlignment,
  TBlockWidthMultiplierKind,
  TMFValueCommandState,
  TVerticalAlignment,
  UpdateControl,
  UpdateMultiFormatText,
  VCXFontNoColorMetaData,
} from "../common/communication.base";
import { NclMultiFormatText } from "../common/components.ncl";
import { VisualContext } from "../common/visualContext";
import { AcquireControl, K2ComponentState, withContext, WithContextPlacementProps, WithVCXProps } from "./k2hoc";
import { K2Img } from "./K2Image";
import { K2TruncateText } from "./K2TruncateText";

interface MultiFormatText extends WithVCXProps {
  texts: Array<MFValue>;
  measure: MFMeasure;
  showError?: boolean;
  errorMessage?: string;
  execute: (commandNumber: number) => void;
}

export class _MFText extends React.PureComponent<MultiFormatText> {
  static displayName = `K2MultiFormatText`;

  render() {
    if (this.props.measure && this.props.texts) {
      let content: JSX.Element;
      if (this.props.showError) {
        content = <p>{this.props.errorMessage}</p>;
      } else {
        content = (
          <div style={{ flexDirection: "column", width: "100%", height: "100%" }}>
            {this.props.measure.Sections.map((section, i) => {
              return this.renderSection(section, i);
            })}
          </div>
        );
      }

      return content;
    }

    return null;
  }

  private renderSection(section: MFSectionMeasureItem, index: number): JSX.Element {
    return (
      <div key={`mf_section_${index}`} style={{ width: "100%" }}>
        {this.renderBlocks(section.LBlocks, -1)}
        <div style={{ flexDirection: "column", width: "100%" }}>
          {section.Bands.map((block, i) => {
            return this.renderBlocks(block, i, true);
          })}
        </div>
        {this.renderBlocks(section.RBlocks, -2)}
      </div>
    );
  }

  private renderBlocks(blocks: MFBlocksMeasureItem, index: number, fill: boolean = false): JSX.Element {
    if (blocks.Height === 0) return null;
    return (
      <div key={`mf_blocks_${index}`} style={{ flex: fill ? "1 0 auto" : "0 0 auto", height: fill ? `${blocks.Height}px` : "100%" }}>
        {blocks.Blocks.map((block) => {
          return this.renderBlock(block);
        })}
      </div>
    );
  }

  private renderBlock(block: MFBlockMeasureItem): JSX.Element {
    let content: JSX.Element;
    if (block.Index < this.props.texts.length) {
      content = this.renderText(block, this.props.texts[block.Index]);
    } else {
      content = <p key={`mf_content_${block.Index}`}>"Bind error"</p>;
    }
    return content;
  }

  private renderText(block: MFBlockMeasureItem, value: MFValue): JSX.Element {
    let cls = "";
    let vcx = this.props.vcx;
    if (block.Attrs.Font && block.Attrs.Font.Enabled) {
      let zoom = block.Attrs.Font.Zoom * 100;
      if (zoom !== vcx.Zoom) {
        vcx = this.props.vcx.createVCXForZoomFactor(zoom);
      }
      cls = this.props.renderer.renderFontRule(vcx.GridControl.Font.cloneWith({ FontStyle: block.Attrs.Font.FontStyle } as VCXFontNoColorMetaData), vcx);
    }

    let style: React.CSSProperties = this.getStyle(block, value, vcx);

    let content: JSX.Element;

    if (value.Text?.length > 0) {
      content = <K2TruncateText line={block.Attrs.LineCount}>{value.Text}</K2TruncateText>;

      if (value.CommandState) {
        content = (
          <div
            style={{ flex: "1 1 auto", opacity: value.CommandState === TMFValueCommandState.vcsEnabled ? 1 : 0.6 }}
            className="button"
            onClick={() => this.props.execute(block.CommandNumber)}
          >
            {content}
          </div>
        );
      }
    }

    if (block.Attrs.ShowImage) {
      let widthHeight = vcx.GridControl.GetRowHeight(block.Attrs.LineCount);

      if (widthHeight > this.props.vcx.sizeMap(70)) {
        widthHeight = this.props.vcx.sizeMap(70);
      }

      if (block.Attrs.ImageHorzAlign === TAlignment.taLeftJustify) {
        content = (
          <>
            <K2Img glyphId={value.GlyphId} vcx={vcx} width={widthHeight} height={widthHeight} {...this.props} />
            {content}
          </>
        );
      } else if (block.Attrs.ImageHorzAlign === TAlignment.taRightJustify) {
        content = (
          <>
            {content}
            <K2Img glyphId={value.GlyphId} vcx={vcx} width={widthHeight} height={widthHeight} {...this.props} />
          </>
        );
      } else {
        content = (
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
              <K2Img glyphId={value.GlyphId} vcx={vcx} width={widthHeight} height={widthHeight} {...this.props} />
            </div>
            <div style={{ position: "relative" }}>{content}</div>
          </div>
        );
      }
    }

    return (
      <div key={`mf_content_${block.Index}`} style={style} className={cls}>
        {content}
      </div>
    );
  }

  private getStyle(block: MFBlockMeasureItem, value: MFValue, vcx: VisualContext): React.CSSProperties {
    let result: React.CSSProperties = { height: "100%" };

    if (!block.Attrs.Enabled) return result;

    if (block.Attrs.Border) {
      result.borderStyle = "solid";
      result.borderBottomWidth = result.borderTopWidth = result.borderRightWidth = result.borderLeftWidth = "0px";
      result.borderColor = vcx.getColor(vcx.Data.ColorMap.ContentFrame1);
      if (block.Attrs.Border.Top) {
        result.borderTopWidth = block.Attrs.Border.Width;
      }
      if (block.Attrs.Border.Bottom) {
        result.borderBottomWidth = block.Attrs.Border.Width;
      }
      if (block.Attrs.Border.Left) {
        result.borderLeftWidth = block.Attrs.Border.Width;
      }
      if (block.Attrs.Border.Right) {
        result.borderRightWidth = block.Attrs.Border.Width;
      }
    }

    switch (block.Attrs.TextHorzAlign) {
      case TAlignment.taCenter:
        result.justifyContent = "center";
        break;
      case TAlignment.taLeftJustify:
        result.justifyContent = "flex-start";
        break;
      case TAlignment.taRightJustify:
        result.justifyContent = "flex-end";
        break;
    }

    switch (block.Attrs.TextVertAlign) {
      case TVerticalAlignment.taAlignBottom:
        result.alignItems = "flex-end";
        break;
      case TVerticalAlignment.taAlignTop:
        result.alignItems = "flex-start";
        break;
      case TVerticalAlignment.taVerticalCenter:
        result.alignItems = "center";
        break;
    }

    switch (block.WidthMultiplierKind) {
      case TBlockWidthMultiplierKind.bwmkByRatio:
        result.width = "100%";
        break;
      case TBlockWidthMultiplierKind.bwmkDigit:
        if (block.Attrs.Width >= 0) result.minWidth = `${vcx.GridControl.Font._0Width * block.Width}px`;
        break;
      case TBlockWidthMultiplierKind.bwmkEm:
        if (block.Attrs.Width >= 0) result.minWidth = `${vcx.GridControl.Font._MWidth * block.Width}px`;
        break;
      case TBlockWidthMultiplierKind.bwmkPercent:
        if (block.Attrs.Width >= 0) result.width = `${block.Width}%`;
        break;
      case TBlockWidthMultiplierKind.bwmkRowHeight:
        if (block.Attrs.Width >= 0) result.minWidth = `${vcx.GridControl.GetRowHeight(block.Width)}px`;
        break;
      case TBlockWidthMultiplierKind.bwmkTextWidth:
        if (value) {
          result.minWidth = `${vcx.GridControl.Font.computeTextWidth(value.Text)}px`;
        }
        break;
      default:
        break;
    }

    if (block.Attrs.Font.Color.ColorMode === FormatterColorMode.fcmColor) {
      result.color = this.props.vcx.getColor(block.Attrs.Font.Color.Color);
    }

    if (block.Attrs.BackColor.ColorMode === FormatterColorMode.fcmColor) {
      result.backgroundColor = this.props.vcx.getColor(block.Attrs.BackColor.Color);
    }

    result.display = "flex";
    result.whiteSpace = "nowrap";
    result.overflow = "hidden";
    result.textOverflow = "ellipsis";

    if (block.BlockDeclaredLF > 0 || block.Attrs.LineCount > 1) {
      result.wordBreak = "break-word";
      result.whiteSpace = "break-spaces";
    }

    result.minHeight = `${vcx.GridControl.GetRowHeight(block.Attrs.LineCount)}px`;
    result.lineHeight = `${vcx.GridControl.GetRowHeight(1)}px`;

    return result;
  }
}

export const K2MFText = withContext(_MFText);

class _MultiFormatText extends React.PureComponent<WithContextPlacementProps, K2ComponentState<UpdateMultiFormatText>> {
  static displayName = `K2MultiFormatText`;
  private control: NclMultiFormatText;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclMultiFormatText;
    }) as NclMultiFormatText;
    this.state = { data: this.control.init(this) as UpdateMultiFormatText, vcxVersion: -1 };
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateMultiFormatText>) => {
      return { data: state as UpdateMultiFormatText };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  render(): JSX.Element {
    if (this.state.data.Measure && this.state.data.Values) {
      return (
        <div style={{ backgroundColor: this.control.VCX.getColor(this.control.VCX.Data.ColorMap.ContentColorBck1), height: "100%", width: "100%" }}>
          <K2MFText
            texts={this.state.data.get("Values").toJS()}
            measure={this.state.data.get("Measure").toJS()}
            {...this.props}
            vcx={this.control.VCX}
            execute={this.control.executeCommandByNumber}
          />
        </div>
      );
    }
    return null;
  }
}

export const K2MultiFormatFText = withContext(_MultiFormatText);
